import { type CSSProperties } from "react";

import { cn } from "@/lib/utils";

/**
 * Cubes from the Exagens logo, vectorized in Figma (256×256 artboard units).
 * `center` is the cube's centre on that artboard; `position` and `swap` come
 * from the "loading" Lottie (lottiefiles.com/free-animation/loading-THVEuupEGG,
 * 800×600 canvas): where each dot starts and how far it travels before coming
 * back.
 */
const LOGO_CUBES = {
  /** Small purple cube: the Lottie's smallest dot. */
  PURPLE: {
    color: "#632e90",
    faceColor: "#8a62ac",
    center: [170.1, 181.6],
    position: [263.776, 342.564],
    swap: [281.94, 12.882],
    base: "M145.484 156.697C149.567 152.337 155.019 143.867 161.375 144.107C170.709 145.222 180.471 148.307 189.778 149.928C198.16 151.387 198.859 155.936 201.053 163.066C201.552 164.713 202.15 166.62 202.675 168.335C203.767 171.547 208.235 184.48 208.412 187.203C208.534 188.858 208.199 190.514 207.443 191.992C206.03 194.809 199.073 201.805 196.538 204.708C194.078 207.572 190.84 210.889 188.524 213.67C181.878 221.649 177.968 218.896 169.159 217.252C162.273 215.967 154.935 213.879 147.99 212.728C142.661 210.664 141.635 207.465 140.059 202.403C139.084 199.274 138.068 196.083 137.089 192.948C135.471 189.34 132.679 179.358 131.883 175.664C130.896 171.085 142.225 160.312 145.484 156.697Z",
    faces: [
      "M147.99 212.729C148.911 210.827 147.133 208.085 149.021 205.374C151.077 202.421 163.229 188.279 166.277 187.82C167.307 187.665 168.409 187.741 169.434 187.889C172.578 188.343 175.687 189.27 178.798 189.922C182.06 190.607 185.623 191.011 188.774 192.081C189.784 192.425 190.804 192.877 191.538 193.677C193.834 196.184 194.515 203.352 196.418 204.635C196.457 204.661 196.498 204.683 196.538 204.708C194.078 207.573 190.84 210.889 188.524 213.671C181.878 221.65 177.968 218.897 169.159 217.252C162.273 215.967 154.935 213.88 147.99 212.729Z",
      "M161.375 144.107C170.709 145.222 180.471 148.307 189.778 149.928C198.16 151.387 198.859 155.936 201.053 163.066C201.552 164.713 202.15 166.62 202.675 168.335C201.265 168.51 197.14 175.488 193.776 175.009C186.777 174.011 179.285 172.068 172.373 170.635C167.589 169.643 166.066 160.512 164.574 156.416C163.487 153.434 161.385 147.175 161.375 144.107Z",
      "M145.484 156.697C147.552 160.203 149.341 169.265 151.126 173.548C153.005 178.06 142.058 186.793 139.594 190.296C139.203 190.851 137.257 192.394 137.089 192.948C135.471 189.34 132.679 179.358 131.883 175.664C130.896 171.085 142.225 160.312 145.484 156.697Z",
    ],
  },
  /** Large orange cube: the Lottie's largest dot. */
  ORANGE: {
    color: "#ef4624",
    faceColor: "#f3745c",
    center: [77.9, 113.85],
    position: [361.777, 165.918],
    swap: [-146.993, 240.92],
    base: "M60.8072 44.6228C63.7642 44.606 70.1677 46.1533 73.2847 46.8193L92.0692 50.8395L109.535 54.5853C113.141 55.359 116.957 55.9668 120.444 57.179C122.823 58.006 125.038 59.4663 126.818 61.2403C131.474 65.8795 135.639 82.339 137.847 89.2873L144.337 109.292C145.674 113.414 148.273 120.38 148.525 124.425C148.734 127.429 148.111 130.433 146.724 133.106C145.319 135.855 142.841 138.669 140.71 140.913C135.759 146.127 131.085 151.868 126.086 157.014C123.286 160.419 119.91 163.994 116.927 167.267C112.942 171.641 108.672 177.098 103.913 180.53C96.7585 185.689 85.9395 181.813 77.9702 180.097L58.698 175.956C53.094 174.765 42.7115 172.909 37.5335 171.433C36.3225 171.069 34.8445 170.564 33.7195 169.996C30.989 168.651 28.6627 166.607 26.9767 164.073C24.7644 160.719 22.9661 154.174 21.6601 150.094C20.0646 145.046 18.4388 140.007 16.7826 134.978L11.1282 117.612C4.89852 98.4788 5.18124 98.1243 18.6922 83.12L32.5155 67.808C37.0475 62.857 47.3305 50.176 52.3987 47.1493C55.0167 45.5855 57.7917 44.8865 60.8072 44.6228Z",
    faces: [
      "M37.5335 171.433L37.8343 171.262C38.3048 170.463 36.8593 166.432 36.8378 164.921C36.7028 155.397 49.1725 147.254 54.4898 139.871C57.5755 135.587 62.346 131.523 65.7973 127.746C68.5828 125.175 72.9745 124.732 76.441 125.5C88.5503 128.182 100.754 130.429 112.768 133.427C115.107 134.042 116.34 135.456 117.913 136.805C121.077 139.518 123.782 155.38 126.086 157.014C123.286 160.419 119.91 163.994 116.927 167.267C112.942 171.641 108.672 177.098 103.913 180.53C96.7585 185.689 85.9395 181.813 77.9703 180.097L58.698 175.957C53.094 174.765 42.7115 172.909 37.5335 171.433Z",
      "M60.8073 44.6228C63.7643 44.606 70.1678 46.1533 73.2848 46.8193L92.0693 50.8395L109.535 54.5853C113.141 55.359 116.958 55.9668 120.444 57.179C122.823 58.006 125.038 59.4663 126.818 61.2403C131.474 65.8795 135.64 82.339 137.847 89.2873C135.531 89.3725 128.498 102.254 121.763 101.756C114.036 101.184 107.03 99.02 99.4235 97.5503C93.5902 96.4233 87.7245 95.0623 81.9473 93.6788C77.72 92.5528 75.1367 90.231 73.7002 86.0873C69.5502 74.1163 65.7825 61.9795 61.9735 49.8943C61.6755 48.949 61.6233 46.0653 61.575 44.9488L60.8073 44.6228Z",
      "M16.7826 134.977L11.1282 117.612C4.89852 98.4787 5.18124 98.1242 18.6922 83.12L32.5155 67.808C34.2035 70.8677 35.6685 76.915 36.803 80.416C38.6077 85.9855 41.9642 94.6557 42.8832 100.203C43.1345 101.719 43 103.28 42.5802 104.758C42.11 106.413 41.1252 107.968 40.0877 109.328C38.0987 111.936 17.9051 134.341 16.9471 134.889C16.893 134.92 16.8375 134.948 16.7826 134.977Z",
    ],
  },
  /** Medium green cube: the Lottie's medium dot, drawn on top. */
  GREEN: {
    color: "#62b446",
    faceColor: "#89c674",
    center: [200.85, 83.75],
    position: [456.419, 423.252],
    swap: [-84.716, -255.739],
    base: "M189.657 36.735C201.962 38.3485 215.525 42.5522 227.847 44.7852C230.445 45.256 233.318 46.9232 234.875 49.1052C237.966 54.2327 239.709 61.5467 241.552 67.2702C242.844 71.377 244.162 75.4757 245.506 79.5655C247.092 84.3945 250.55 92.0902 247.476 96.7752C243.613 102.663 238.002 107.853 233.38 113.261C229.075 118.008 219.628 130.906 213.01 130.803C206.953 130.709 198.543 128.034 192.395 126.976C186.139 125.618 179.586 124.38 173.546 122.933C172.393 122.606 171.523 122.3 170.451 121.74C168.639 120.772 167.115 119.342 166.032 117.596C164.302 114.843 160.596 102.298 159.281 98.2207C149.323 70.1127 150.077 73.901 169.976 52.4182C172.872 49.2265 175.749 46.0185 178.609 42.7942C181.903 39.1532 184.489 36.9927 189.657 36.735Z",
    faces: [
      "M173.546 122.933C173.956 121.919 172.304 118.001 173.229 115.265C174.145 112.554 191.749 93.241 194.539 91.9312C195.794 91.342 197.607 91.5015 198.969 91.641C202.922 92.0462 207.005 93.214 210.895 94.0412C215.289 94.9755 220.063 95.6075 224.272 97.1275C225.341 97.5132 226.5 97.998 227.332 98.7895C230.516 101.816 231.46 109.19 233.38 113.261C229.075 118.008 219.628 130.906 213.01 130.803C206.953 130.709 198.543 128.034 192.395 126.976C186.139 125.618 179.585 124.38 173.546 122.933Z",
      "M189.657 36.735C201.962 38.3485 215.525 42.5522 227.847 44.7852C230.445 45.256 233.318 46.9232 234.875 49.1052C237.966 54.2327 239.708 61.5467 241.552 67.2702C237.955 69.031 235.318 75.8045 230.335 75.4855C224.569 75.1165 218.899 73.2327 213.105 72.186C209.237 71.487 203.558 70.538 200.171 68.47C198.273 66.3497 196.911 62.72 196.207 59.9802C194.388 52.8972 189.955 43.8365 189.657 36.735Z",
      "M159.281 98.2207C149.323 70.1127 150.077 73.901 169.976 52.4182C170.615 53.3625 176.201 71.0057 176.673 73.0892C176.94 74.265 177.117 75.5015 176.893 76.699C175.939 81.7885 163.034 93.4725 159.281 98.2207Z",
    ],
  },
} as const;

type LogoCube = (typeof LOGO_CUBES)[keyof typeof LOGO_CUBES];

/** Bottom to top, matching the Lottie's layer order. */
const PAINT_ORDER: readonly LogoCube[] = [
  LOGO_CUBES.PURPLE,
  LOGO_CUBES.ORANGE,
  LOGO_CUBES.GREEN,
];

/** Artboard units → Lottie units, so the orange cube is as wide as the largest dot. */
const CUBE_SCALE = 1.669;

const swapStyle = ({ swap: [x, y] }: LogoCube): CSSProperties =>
  ({
    "--cube-swap-x": `${x}px`,
    "--cube-swap-y": `${y}px`,
  }) as CSSProperties;

const cubeTransform = ({
  center: [cx, cy],
  position: [x, y],
}: LogoCube): string =>
  `translate(${x} ${y}) scale(${CUBE_SCALE}) translate(${-cx} ${-cy})`;

/**
 * Loading indicator: the three logo cubes swap places (2s loop). Colours are
 * fixed, so it works before the institution theme loads.
 */
const LoadingLogoCubes = ({ className }: { className?: string }) => (
  <svg
    viewBox="90 40 530 495"
    className={cn("h-80 w-auto", className)}
    role="status"
    aria-busy
    aria-label="Loading"
  >
    {PAINT_ORDER.map((cube) => (
      <g
        key={cube.color}
        className="
          animate-loading-cube-swap
          motion-reduce:animate-none
        "
        style={swapStyle(cube)}
      >
        <g transform={cubeTransform(cube)}>
          <path d={cube.base} fill={cube.color} />
          {cube.faces.map((face) => (
            <path key={face} d={face} fill={cube.faceColor} />
          ))}
        </g>
      </g>
    ))}
  </svg>
);

export default LoadingLogoCubes;
